/**
 * @file footer-link-where.ts
 * @description «Где это настраивается» у ссылок нижней полосы подвала
 * (phase19.16, решение пользователя 2026-10-11 — SB121): оферта, политика
 * ПДн и условия возврата — «Юридические документы» (с состоянием
 * документа), «Условия доставки и оплаты» — страница из «Настройки сайта →
 * Подвал», «Личный кабинет» — системная страница /lk.
 *
 * Реестр по ссылке (OCP): новая ссылка платформы — запись в `WHERE`, tsc
 * не даст забыть. Чистая функция — проверяется тестом.
 *
 * Партия «Юридические документы» (SB142; uiux-legal-docs §5, §7): свои
 * строки блока «Ссылки» — `footerDocRowView`: название документа или группы,
 * вид, пометка «не опубликован · Юридические документы →» или «документ
 * удалён — уберите строку».
 */

import {
  FOOTER_DOC_ITEM_STATUS_LABELS,
  footerDocItemStatus,
  LEGAL_FOOTER_LINK_SLUGS,
  LEGAL_INDEX_LABEL,
  type FooterDocItem,
  type FooterDocItemStatus,
  type FooterPlatformLink,
} from "@chaiberi/shared";

import type { LegalTarget } from "../api-legal-targets";
import type { SitePageRef } from "./types";

/** Юридические документы, на которые ведут ссылки платформы. */
export type FooterLegalDocKey = "offer" | "privacy" | "returns";

/** Адреса документов ссылок платформы — из shared (один источник с сервером). */
export const FOOTER_LEGAL_DOC_SLUGS: Readonly<Record<FooterLegalDocKey, string>> = LEGAL_FOOTER_LINK_SLUGS;

/** Состояние документов: `published` или `draft`; нет ключа — документа нет; null — не загружено. */
export type FooterLegalDocsState = Partial<Record<FooterLegalDocKey, "published" | "draft">> | null;

/** Подпись «где задаётся»: текст перед ссылкой и сама ссылка (null — без ссылки). */
export interface FooterLinkWhere {
  note: string | null;
  linkLabel: string | null;
  href: string | null;
}

export interface FooterLinkWhereInput {
  docs: FooterLegalDocsState;
  deliveryPage: SitePageRef | null;
  /** `/shop/<slug>/settings/legal-documents`. */
  legalDocsHref: string;
  /** «Настройки сайта → Подвал» в конструкторе (поле страницы условий доставки). */
  siteFooterSettingsHref: string;
}

function docWhere(key: FooterLegalDocKey, input: FooterLinkWhereInput): FooterLinkWhere {
  const state = input.docs?.[key];
  const note = input.docs === null ? null : state === "published" ? null : state === "draft" ? "не опубликован" : "не создан";
  return { note, linkLabel: "Юридические документы →", href: input.legalDocsHref };
}

const WHERE: Readonly<Record<FooterPlatformLink, (input: FooterLinkWhereInput) => FooterLinkWhere>> = {
  offer: (i) => docWhere("offer", i),
  privacy: (i) => docWhere("privacy", i),
  returns: (i) => docWhere("returns", i),
  delivery: (i) => ({
    note: i.deliveryPage
      ? `Страница: «${i.deliveryPage.title}»${i.deliveryPage.isPublished ? "" : " · не опубликована"}`
      : "Страница не выбрана",
    linkLabel: "Настройки сайта → Подвал →",
    href: i.siteFooterSettingsHref,
  }),
  account: () => ({ note: "системная страница /lk", linkLabel: null, href: null }),
};

export function footerLinkWhere(input: FooterLinkWhereInput): Record<FooterPlatformLink, FooterLinkWhere> {
  return {
    offer: WHERE.offer(input),
    privacy: WHERE.privacy(input),
    returns: WHERE.returns(input),
    delivery: WHERE.delivery(input),
    account: WHERE.account(input),
  };
}

// ── Свои строки «Ссылок» (партия «Юридические документы», SB142) ────────────

/** Строка своего документа в списке «Ссылки»: подпись, вид, состояние и «где задаётся». */
export interface FooterDocRowView {
  label: string;
  /** Вид словами: «группа», «PDF»; null — у текста и «Все документы» не пишем. */
  kind: string | null;
  status: FooterDocItemStatus;
  /** Пометка состояния: «не опубликован», «пуста — на сайте не видна», «документ удалён — уберите строку». */
  note: string | null;
  where: FooterLinkWhere | null;
}

const DOC_KIND_NOTE: Readonly<Record<LegalTarget["kind"], string | null>> = { TEXT: null, PDF: "PDF", GROUP: "группа" };

/**
 * Вид своей строки по списку документов магазина (`null` — ещё не загружен:
 * без пометок, подпись «Документ»). Состояние — общее правило shared
 * `footerDocItemStatus` (выключена / видна / не опубликован / удалён).
 */
export function footerDocRowView(item: FooterDocItem, targets: readonly LegalTarget[] | null, legalDocsHref: string): FooterDocRowView {
  const where: FooterLinkWhere = { note: null, linkLabel: "Юридические документы →", href: legalDocsHref };
  if (targets === null) {
    const label = item.kind === "allDocuments" ? LEGAL_INDEX_LABEL : "Документ";
    return { label, kind: null, status: item.enabled ? "ok" : "off", note: item.enabled ? null : FOOTER_DOC_ITEM_STATUS_LABELS.off, where };
  }
  const visible = new Map(targets.map((t) => [t.id, { visible: t.visibleOnSite }] as const));
  const status = footerDocItemStatus(item, visible);
  if (item.kind === "allDocuments") {
    const note = status === "hidden" ? "нет опубликованных документов — на сайте не видна" : FOOTER_DOC_ITEM_STATUS_LABELS[status] || null;
    return { label: LEGAL_INDEX_LABEL, kind: null, status, note, where };
  }
  const target = targets.find((t) => t.id === item.documentId) ?? null;
  if (target === null) return { label: "Удалённый документ", kind: null, status, note: FOOTER_DOC_ITEM_STATUS_LABELS[status] || null, where: null };
  const groupEmpty = status === "hidden" && target.kind === "GROUP" && target.status !== "draft" && target.status !== "unpublished";
  const note = groupEmpty ? "пуста — на сайте не видна" : FOOTER_DOC_ITEM_STATUS_LABELS[status] || null;
  return { label: target.title, kind: DOC_KIND_NOTE[target.kind], status, note, where };
}
